import { ColumnDef } from '@tanstack/react-table'
import { Check, ExternalLink, Loader2, RotateCw, Search } from 'lucide-react'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'

import { ImageContainer } from '@/components'
import { IconActionButton } from '@/components/common/buttons/icon-action-button'
import { Table } from '@/components/common/data-display/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDebounce } from '@/hooks/useDebounce'
import { Asset, Equipment } from '@/services/backend/config/entities'

import { AssetService } from '../services/asset.service'

type Extended = Asset & {
  Equipment?: Equipment
}

type Props = {
  tipoEquipamento: 'EQUIPAMENTO' | 'BATERIA' | undefined
  disponivel?: boolean
  onSelect: (data: Extended) => void
}

function Form({ tipoEquipamento, disponivel, onSelect }: Props) {
  const { value, setValue, debounceValue: patrimonio } = useDebounce('')
  const { data, isFetching, refetch } = useQuery(
    ['asset-searchModal', { patrimonio, tipoEquipamento }],
    async () => {
      return await AssetService.search({
        patrimonio,
        tipoEquipamento,
        disponivel,
        manutencao: false,
        ativo: true,
      })
    },
  )

  const columns: ColumnDef<Extended>[] = [
    {
      header: 'Nome',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div
          className="flex items-center gap-2"
          style={{ opacity: row.original.active ? 1 : 0.5 }}
        >
          <ImageContainer
            src={row.original?.Equipment?.imageStr ?? undefined}
            className="size-16"
          />
          <div>
            <p>
              {row.original.assetNumber}
              <Link
                to={`/app/entity/patrimonios/${row.original.id}/detalhes`}
                target="_blank"
              >
                <ExternalLink className="ml-2 inline size-3 pt-[2px] text-primary" />
              </Link>
            </p>
            <p className="text-sm text-muted-foreground">
              {row.original.Equipment?.name ?? row.original.name}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: '',
      accessorKey: 'id',
      enableSorting: false,
      cell: ({ row }) => (
        <IconActionButton
          icon={Check}
          tooltip="Selecionar"
          onClick={() => onSelect(row.original)}
        />
      ),
    },
  ]

  return (
    <>
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

        <Input
          className="px-9"
          placeholder="Pesquisar"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />

        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <IconActionButton
              icon={RotateCw}
              tooltip="Pesquisar"
              variant="ghost"
              onClick={() => refetch()}
            />
          )}
        </div>
      </div>

      <Table
        data={data ?? []}
        columns={columns}
        initialSorting={[{ id: 'name', desc: false }]}
        initialState={{
          pagination: {
            pageSize: 5,
          },
        }}
      />
    </>
  )
}

export function Asset_SearchModal({
  tipoEquipamento,
  disponivel,
  onSelect,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <IconActionButton icon={Search} tooltip="Pesquisar" />
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pesquisar</DialogTitle>
        </DialogHeader>

        <Form
          tipoEquipamento={tipoEquipamento}
          disponivel={disponivel}
          onSelect={(res) => {
            onSelect(res)
            setIsOpen(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
